/**
 * What a lesson still needs before «Завершить урок», and the state of an exercise's three steps.
 *
 * KEEP IN SYNC with the server rule: services/astro-bot/src/services/course/rules.ts (`lessonCompletion`).
 * An exercise counts when its answer exists, `text.trim() !== ''` and `selfMark` is set; a quiz question counts
 * when its id is in `quizCorrect`. The server re-checks on POST complete; this copy only explains the disabled
 * button. tests/unit/lib/course-completion.test.ts runs both on the same cases.
 */
import type { SelfMark } from '../types/course';

export type MissingItem =
  | { kind: 'answer' | 'mark'; id: string; title: string }
  | { kind: 'quiz'; id: string; number: number };

type SavedAnswer = { text: string; selfMark: SelfMark | null };

/** Exercises first (no saved answer → 'answer', saved without a mark → 'mark'), then quiz questions, in lesson order. */
export function lessonMissing(
  content: { exercises: readonly { id: string; title: string }[]; quiz: readonly { id: string }[] },
  answers: Readonly<Record<string, SavedAnswer | undefined>>,
  quizCorrect: readonly string[],
): MissingItem[] {
  const out: MissingItem[] = [];
  for (const ex of content.exercises) {
    const a = Object.prototype.hasOwnProperty.call(answers, ex.id) ? answers[ex.id] : undefined;
    if (!a || a.text.trim() === '') out.push({ kind: 'answer', id: ex.id, title: ex.title });
    else if (!a.selfMark) out.push({ kind: 'mark', id: ex.id, title: ex.title });
  }
  const right = new Set(quizCorrect);
  content.quiz.forEach((q, i) => {
    if (!right.has(q.id)) out.push({ kind: 'quiz', id: q.id, number: i + 1 });
  });
  return out;
}

export type StepState = 'done' | 'current' | 'later';

/**
 * Steps of one exercise card: 1 write and save, 2 open the solution, 3 mark.
 * 1 done = a non-blank saved answer and the field equals it; 2 done = 1 done and (solution open or marked);
 * 3 done = 1 done and marked. The first step not done is current; the rest are later.
 */
export function exerciseSteps(s: { savedText: string; text: string; solutionOpen: boolean; selfMark: SelfMark | null }): [StepState, StepState, StepState] {
  const saved = s.savedText.trim() !== '' && s.text === s.savedText;
  const done = [saved, saved && (s.solutionOpen || !!s.selfMark), saved && !!s.selfMark];
  const current = done.indexOf(false);
  return done.map((d, i) => (d ? 'done' : i === current ? 'current' : 'later')) as [StepState, StepState, StepState];
}
